<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useStoresStore } from '@/entities/store'
// CButton is used in the template
import CAddStoreDialog from '@/features/clients/ui/CAddStoreDialog.vue'
import { getCookie, setCookie } from '@/shared/lib/utils'
import { CButton } from '@/shared/ui'
import CUpdateApiKeyModal from '@/shared/ui/CUpdateApiKeyModal.vue'
import CCompanySwitcher from './CCompanySwitcher.vue'

withDefaults(defineProps<Props>(), {
  totalAmount: '65412.5 s',
})

const emit = defineEmits<{
  close: []
}>()

const route = useRoute()
const { t } = useI18n()

const showApiKeyModal = ref(false)

const storeStore = useStoresStore()

const { stores, isLoading: isStoresLoading } = storeToRefs(storeStore)

const showAddStoreDialog = ref(false)
const companySwitcherRef = ref<InstanceType<typeof CCompanySwitcher> | null>(
  null,
)

// Handle navigation click - emit close event for drawer
function handleNavClick() {
  emit('close')
}

// Handle store creation success
function handleStoreCreated(storeId: string) {
  // Update the company switcher to show the new store
  if (companySwitcherRef.value) {
    companySwitcherRef.value.updateToNewStore(storeId)
  }

  // Refresh the page to refetch all data with the new store context
  setTimeout(() => {
    window.location.reload()
  }, 500) // Small delay to ensure the store update is processed
}

// Store selector logic
const currentStoreId = ref<string>(getCookie('storeId') || '')

// Watch for stores changes and update currentStoreId if needed
watch(
  [stores, isStoresLoading],
  ([newStores, loading]) => {
    if (newStores.length > 0) {
      const cookieStoreId = getCookie('storeId')
      // If no store is selected or the selected store doesn't exist, select the first store
      if (
        !cookieStoreId
        || !newStores.some(store => store.id.toString() === cookieStoreId)
      ) {
        const firstStoreId = newStores[0].id.toString()
        currentStoreId.value = firstStoreId
        setCookie('storeId', firstStoreId)
      }
      else {
        currentStoreId.value = cookieStoreId
      }
    }
    else if (!loading) {
      // Only surface the empty state once the query has resolved —
      // otherwise cold-start/mobile-resume flashes the dialog before
      // stores hydrate (clients see a ghost "Add store" modal on refresh).
      if (route.name === 'Dashboard') {
        showAddStoreDialog.value = true
      }
    }
  },
  { immediate: true },
)

const menu = [
  {
    name: 'dashboard',
    icon: 'icon-grid',
    routeName: 'Dashboard',
  },
  {
    name: 'profit_calculator',
    icon: 'icon-calculate',
    routeName: 'ProfitCalculator',
  },
  {
    name: 'reviews_management',
    icon: 'icon-mailbox',
    routeName: 'ReviewsManagement',
  },
  {
    name: 'supply_management',
    icon: 'icon-truck',
    routeName: 'SupplyManagement',
  },
  {
    name: 'repacker',
    icon: 'icon-pie-chart',
    routeName: 'Repacker',
  },
  {
    name: 'auto_bidder',
    icon: 'icon-megaphone',
    routeName: 'AutoBidder',
  },
  {
    name: 'ai',
    icon: 'icon-ai-beautify',
    routeName: 'AI',
  },
  // {
  //   name: "suppliers_catalog",
  //   icon: "icon-list-bullet",
  //   routeName: "SuppliersCatalog"
  // },
  {
    name: 'services_and_courses',
    icon: 'icon-file-verified',
    routeName: 'ServicesAndCourses',
  },
  {
    name: 'suggest_idea',
    icon: 'icon-lightbulb-outline',
    routeName: 'SuggestIdea',
  },
  {
    name: 'tariffs',
    icon: 'icon-cash',
    routeName: 'Tariffs',
  },
  // Notifications menu item - Hidden
  // {
  //   name: "notifications",
  //   icon: "icon-information-circle",
  //   routeName: "Notifications"
  // }
]

interface Props {
  totalAmount?: string
}

const currentYear = new Date().getFullYear()
</script>

<template>
  <!-- Sidebar Content Component -->
  <div class="sm:w-64 w-full h-full flex flex-col overflow-x-hidden">
    <div class="sm:p-4 p-2">
      <RouterLink to="/dashboard" class="sm:p-4 p-2 block sm:mb-0 mb-2">
        <img
          src="@/shared/assets/svg/logo.svg"
          alt="logo"
          class="sm:h-auto h-7"
        >
      </RouterLink>

      <CCompanySwitcher
        ref="companySwitcherRef"
        @add-store="showAddStoreDialog = true"
      />

      <nav class="flex-1">
        <ul class="flex flex-col sm:gap-2 gap-1">
          <li v-for="item in menu" :key="item.name">
            <RouterLink
              :to="{ name: item.routeName }"
              class="flex items-center gap-2 sm:py-2.5 sm:px-4 py-2 px-2 rounded-xl border border-transparent text-text-primary"
              :class="{
                '!border-border-lighter bg-white': route.name === item.routeName,
              }"
              @click="handleNavClick"
            >
              <i :class="item.icon" class="text-2xl" />
              <span class="font-medium text-[13px]">{{ t(item.name) }}</span>
            </RouterLink>
          </li>
        </ul>
      </nav>

      <!-- Mobile/Tablet Only: Buttons and Tariff -->
      <div
        class="md:hidden mt-auto py-2 border-y border-border-lighter space-y-3"
      >
        <!-- API Key -->
        <CButton
          type="primary"
          class="h-10 w-full"
          @click="showApiKeyModal = true"
        >
          <i class="icon-pencil-edit-01 text-2xl" />
          {{ t("api_key") }}
        </CButton>

        <!-- Tariff Block -->
        <!-- <div class="flex items-center gap-2 bg-white rounded-xl p-0.5 pl-3">
          <i
            class="icon-crown-minimalistic-outline text-2xl text-text-secondary"
          ></i>
          <span class="text-text-primary font-semibold text-sm leading-[22px]">
            {{ t("essential") }}
          </span>
          <span class="text-text-regular text-sm leading-[22px]">
            {{ t("unlimited") }}
          </span>
          <CButton class="!h-9">
            <i class="icon-cash text-2xl"></i>
            {{ t("buy") }}
          </CButton>
        </div> -->
      </div>

      <div class="flex md:hidden flex-col py-2 space-y-4">
        <div class="flex flex-col items-start gap-4">
          <ElLink
            href="https://t.me/marketassist_support"
            size="small"
            target="_blank"
          >
            <i class="icon-telegram text-2xl" />
          </ElLink>
        </div>
        <div class="text-xs text-gray-600">
          {{ $t("footer_copyright", { year: currentYear }) }}
        </div>
      </div>
    </div>
  </div>

  <!-- Add Store Dialog -->
  <CAddStoreDialog
    v-model="showAddStoreDialog"
    @store-created="handleStoreCreated"
  />

  <!-- API Key Modal -->
  <Teleport to="body">
    <CUpdateApiKeyModal v-model="showApiKeyModal" />
  </Teleport>
</template>
